import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Product } from '../../types';
import { getProducts } from '../../services/firebase';
import Icon from '../atoms/Icon';
import './SearchOverlay.css';

interface SearchOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

const SearchOverlay: React.FC<SearchOverlayProps> = ({ isOpen, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Common search suggestions
  const commonSuggestions = [
    'cake',
    'moon cake',
    // 'clouds kids cake',
    'Birthday Cake',
    'Celebration Cake',
    'Custom Cake Flavors'
  ];

  // Load products on component mount
  useEffect(() => {
    const loadProducts = async () => {
      setIsLoading(true);
      try {
        const allProducts = await getProducts();
        setProducts(allProducts.filter(product => product.isActive));
      } catch (error) {
        console.error('Error loading products:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) {
      loadProducts();
    }
  }, [isOpen]);

  // Filter products and suggestions based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredProducts([]);
      setSuggestions(commonSuggestions);
      return;
    }

    const query = searchQuery.toLowerCase();
    
    // Filter products
    const filtered = products.filter(product => 
      product.name.toLowerCase().includes(query) ||
      product.description.toLowerCase().includes(query) ||
      product.collection.toLowerCase().includes(query) ||
      product.flavors.some(flavor => flavor.name.toLowerCase().includes(query))
    );

    setFilteredProducts(filtered);

    // Generate suggestions based on query
    const querySuggestions = commonSuggestions.filter(suggestion =>
      suggestion.toLowerCase().includes(query)
    );

    setSuggestions(querySuggestions);
  }, [searchQuery, products]);

  // Focus input when overlay opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setSearchQuery(suggestion);
  };

  const handleProductClick = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="search-overlay" onClick={onClose}>
      <div className="search-content" onClick={(e) => e.stopPropagation()}>
        {/* Search Header */}
        <div className="search-header">
          <div className="search-input-container">
            <input
              ref={inputRef}
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={handleInputChange}
              className="search-input"
            />
            {searchQuery && (
              <button 
                className="search-clear" 
                onClick={handleClearSearch}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
            <button className="search-submit" aria-label="Search">
              <Icon type="search" size="small" />
            </button>
          </div>
          <button className="search-close" onClick={onClose} aria-label="Close search">
            ×
          </button>
        </div>

        {/* Search Results */}
        <div className="search-results">
          <div className="search-sections">
            {/* Suggestions */}
            <div className="search-section">
              <h3 className="search-section-title">SUGGESTIONS</h3>
              <div className="search-suggestions">
                {suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    className="search-suggestion"
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>

            {/* Products */}
            <div className="search-section">
              <h3 className="search-section-title">PRODUCTS</h3>
              <div className="search-products">
                {isLoading ? (
                  <div className="search-loading">Loading products...</div>
                ) : filteredProducts.length > 0 ? (
                  filteredProducts.slice(0, 6).map((product) => (
                    <Link
                      key={product.id}
                      to={`/catalog/${product.collection}/${product.id}`}
                      className="search-product"
                      onClick={handleProductClick}
                    >
                      <div className="search-product-image">
                        {product.images.length > 0 ? (
                          <img 
                            src={product.images[0].url} 
                            alt={product.name}
                            loading="lazy"
                          />
                        ) : (
                          <div className="search-product-placeholder">
                            <Icon type="image" size="small" />
                          </div>
                        )}
                      </div>
                      <div className="search-product-info">
                        <span className="search-product-name">{product.name}</span>
                      </div>
                    </Link>
                  ))
                ) : searchQuery ? (
                  <div className="search-no-results">No products found</div>
                ) : (
                  <div className="search-start-typing">Start typing to search products</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchOverlay;
